/**
 * Partner Hooks
 *
 * Provides hooks for partner CRUD operations using createResourceHooks.
 * Includes additional custom hooks for search, partner users, etc.
 */

import { useQuery } from '@tanstack/react-query'
import { createResourceHooks } from './createResourceHooks'
import { raClient, type RAListParams } from '@/lib/raClient'
import { createQueryKeys } from '@/lib/queryKeys'
import type {
  Partner,
  PartnerListFilters,
  PartnerCreateInput,
  PartnerUpdateInput,
} from '@/types/partner'
import type { User } from '@/types/user'

// ============================================================================
// Query Keys
// ============================================================================

export const partnerKeys = createQueryKeys('partners')

// ============================================================================
// Base CRUD Hooks (via createResourceHooks)
// ============================================================================

const partnerHooks = createResourceHooks<Partner>('partners', partnerKeys)

// Re-export with more descriptive names
export const usePartnerList = (
  params?: RAListParams & { filters?: PartnerListFilters },
  options?: { enabled?: boolean; staleTime?: number }
) => {
  // Build filter from PartnerListFilters
  const filter: Record<string, unknown> = { ...params?.filter }
  if (params?.filters?.name) {
    filter.name = `%${params.filters.name}%`
  }
  if (params?.filters?.q) {
    filter.q = params.filters.q
  }

  return partnerHooks.useList(
    {
      page: params?.page,
      perPage: params?.perPage,
      sort: params?.sort ?? { field: 'id', order: 'DESC' },
      filter,
    },
    options
  )
}

export const usePartner = partnerHooks.useOne

export const useCreatePartner = () => {
  const mutation = partnerHooks.useCreate()
  return {
    ...mutation,
    mutateAsync: (data: PartnerCreateInput) =>
      mutation.mutateAsync(data as Partial<Partner>),
    mutate: (data: PartnerCreateInput) => mutation.mutate(data as Partial<Partner>),
  }
}

export const useUpdatePartner = () => {
  const mutation = partnerHooks.useUpdate()
  return {
    ...mutation,
    mutateAsync: (data: PartnerUpdateInput) =>
      mutation.mutateAsync({ id: data.id, data }),
    mutate: (data: PartnerUpdateInput) => mutation.mutate({ id: data.id, data }),
  }
}

export const useDeletePartner = partnerHooks.useDelete

// ============================================================================
// Custom Hooks
// ============================================================================

/**
 * Hook to search partners with real-time autocomplete
 */
export function useSearchPartners(searchTerm: string, enabled = true) {
  return useQuery({
    queryKey: [...partnerKeys.all, 'search', searchTerm],
    queryFn: async () => {
      const result = await raClient.getList<Partner>('partners', {
        page: 1,
        perPage: 50,
        sort: { field: 'name', order: 'ASC' },
        filter: searchTerm ? { name: `%${searchTerm}%` } : {},
      })
      return result.data
    },
    enabled,
    staleTime: 30 * 1000,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Hook to fetch users for a partner (for support email selector)
 */
export function usePartnerUsers(partnerId: number, enabled = true) {
  return useQuery({
    queryKey: [...partnerKeys.all, 'users', partnerId],
    queryFn: async () => {
      const result = await raClient.getList<User>('users', {
        page: 1,
        perPage: 100,
        sort: { field: 'first_name', order: 'ASC' },
        filter: { partner_id: partnerId },
      })
      return result.data
    },
    enabled: enabled && !!partnerId,
    staleTime: 30 * 1000,
  })
}
